import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Calendar } from 'react-big-calendar';
import { useTranslation } from 'react-i18next';
import { getCourseCategory } from '../../../LambdaFunctions/shared/courseCategories';
import PublicLessonDetailsModal from './PublicLessonDetailsModal';
import { shiftDate } from './publicTimetableDates';
import { calendarEvents, publicLocalizer, schoolNow, timeBounds, wallDate } from './publicCalendarModel';
import 'react-big-calendar/lib/css/react-big-calendar.css';

function EventWrapper({ children, event }) {
  return React.cloneElement(children, {
    role: 'button',
    tabIndex: 0,
    'aria-label': event.title,
    'data-public-lesson': true,
    onKeyDown: key => { if (key.key === 'Enter' || key.key === ' ') { key.preventDefault(); children.props.onClick(key); } },
    style: { ...children.props.style, width: `calc(${100 / event.lanes}% - 2px)`, left: `${(event.lane * 100) / event.lanes}%` },
  });
}
EventWrapper.propTypes = { children: PropTypes.element.isRequired, event: PropTypes.object.isRequired };

function EventContent({ event }) {
  const { t } = useTranslation();
  const category = getCourseCategory(event.lesson.categoryId);
  return (
    <div className="public-calendar-event">
      <strong>{event.lesson.startTime}–{event.lesson.endTime}</strong>
      {category && <span>{t(category.labelKey)}</span>}
      <span>{event.lesson.level || t(`public:timetable.${event.lesson.kind}`)}</span>
      <span className="public-calendar-event-status">{t(`public:timetable.${event.lesson.availability}`)}</span>
    </div>
  );
}
EventContent.propTypes = { event: PropTypes.object.isRequired };
const components = { event: EventContent, eventWrapper: EventWrapper };
const ignoreNavigation = () => {};

export default function PublicTimetableCalendar({ lessons, weekStart }) {
  const { t, i18n } = useTranslation();
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const mobile = viewportWidth < 768;
  const [desktopView, setDesktopView] = useState('week');
  const daily = mobile || desktopView === 'day';
  const [day, setDay] = useState(() => (schoolNow().getDay() + 6) % 7);
  const [selected, setSelected] = useState(null);
  const opener = useRef(null);
  const closeDetails = () => setSelected(null);
  const restoreFocus = () => { if (opener.current && opener.current.isConnected) opener.current.focus(); };
  useEffect(() => { setSelected(null); }, [lessons, weekStart, mobile]);
  useEffect(() => {
    const resize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  const culture = i18n.language.startsWith('en') ? 'en' : 'pl';
  const days = Array.from({ length: 7 }, (_, offset) => wallDate(shiftDate(weekStart, offset)));
  const date = days[daily ? day : 0];
  const events = useMemo(() => calendarEvents(lessons).map(event => ({
    ...event,
    title: [`${event.lesson.startTime}–${event.lesson.endTime}`, t(`public:timetable.${event.lesson.kind}`),
      getCourseCategory(event.lesson.categoryId) && t(getCourseCategory(event.lesson.categoryId).labelKey), event.lesson.level,
      t(`public:timetable.${event.lesson.availability}`)].filter(Boolean).join(' · '),
  })), [lessons, t, i18n.language]);
  const selectEvent = (event, interaction) => { opener.current = interaction.currentTarget; setSelected(event); };
  const eventStyle = event => ({ backgroundColor: (getCourseCategory(event.lesson.categoryId) || {}).color || '#e9ecef', color: '#000000', borderColor: '#000000' });
  const bounds = timeBounds(events, date);
  return (
    <div className="public-calendar">
      {!mobile && (
        <div className="btn-group mb-3" role="group" aria-label={t('public:timetable.view')}>
          {['week', 'day'].map(view => (
            <button
              key={view}
              type="button"
              className={`btn btn-outline-secondary ${desktopView === view ? 'active' : ''}`}
              aria-pressed={desktopView === view}
              onClick={() => { setDesktopView(view); setSelected(null); }}
            >{t(`public:timetable.${view}`)}
            </button>
          ))}
        </div>
      )}
      {daily && (
        <div className="public-calendar-day-picker">
          <label htmlFor="public-calendar-day">{t('public:timetable.chooseDay')}</label>
          <select id="public-calendar-day" className="form-control" value={day} onChange={event => { setDay(Number(event.target.value)); setSelected(null); }}>
            {days.map((value, index) => <option key={value.getDay()} value={index}>{publicLocalizer.format(value, 'EEEE', culture)}</option>)}
          </select>
          {!lessons.some(lesson => lesson.date === shiftDate(weekStart, day)) && <p role="status">{t('public:timetable.emptyDay')}</p>}
        </div>
      )}
      <PublicLessonDetailsModal event={selected} onHide={closeDetails} onExited={restoreFocus} />
      {daily ? (
        <div className={`public-calendar-list ${daily ? 'public-calendar-list--day' : ''}`}>
          {days.filter((value, index) => index === day).map(value => (
            <section key={value.getDay()} className="public-calendar-list-day" aria-label={publicLocalizer.format(value, 'EEEE', culture)}>
              <h3>{publicLocalizer.format(value, 'EEEE', culture)}</h3>
              <ul>
                {events.filter(event => event.start.getDay() === value.getDay()).map(event => (
                  <li key={event.id}>
                    <button
                      type="button"
                      data-public-lesson
                      className={`public-calendar-list-event public-calendar-event--${event.lesson.availability}`}
                      style={eventStyle(event)}
                      aria-label={event.title}
                      onClick={interaction => selectEvent(event, interaction)}
                    ><EventContent event={event} />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : (
        <Calendar
          localizer={publicLocalizer}
          culture={culture}
          events={events}
          date={date}
          view={daily ? 'day' : 'week'}
          views={['week', 'day']}
          toolbar={false}
          onNavigate={ignoreNavigation}
          onView={ignoreNavigation}
          drilldownView={null}
          min={bounds.min}
          max={bounds.max}
          scrollToTime={wallDate(weekStart, '00:00')}
          getNow={schoolNow}
          style={{ height: (Math.ceil((bounds.max - bounds.min) / 3600000) + 1) * 108 + 60 }}
          step={15}
          timeslots={4}
          selectable={false}
          components={components}
          onSelectEvent={selectEvent}
          eventPropGetter={event => ({
            className: `public-calendar-event--${event.lesson.availability}`,
            style: eventStyle(event),
          })}
        />
      )}

    </div>
  );
}
PublicTimetableCalendar.propTypes = { lessons: PropTypes.arrayOf(PropTypes.object).isRequired, weekStart: PropTypes.string.isRequired };
